import React, { useEffect, useState } from "react";
import instance from "../../../../axios/instance";
import { getToken } from "../../../../utils/token";
import {
  useLoaderData,
  useNavigate,
  useRevalidator,
  useSearchParams,
} from "react-router-dom";
import EarningsChart from "../components/EarningsChart";
import { ArrowLeft2, ArrowRight2, SearchNormal, Sort } from "iconsax-react";
import { PaginationButton } from "../../../../components/PaginationButton";
import dayjs from "dayjs";

export const paymentsLoader = async () => {
  const token = getToken();
  const payments = await instance.get(
    "/doctor/connect/payments",
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return {
    payments: payments.data,
  };
};

export const PaymentsPage = () => {
  const { payments } = useLoaderData();
  const [filteredPayments, setFilteredPayments] = useState(payments);
  const [_payments, setPayments] = useState(
    [...payments].map((_, index) => ({
      ..._,
      index: index + 1,
    }))
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q");
  const [searchQuery, setSearchQuery] = useState(query || "");
  const [pageIndex, setPageIndex] = useState(1);
  const [sorted, setSorted] = useState("");
  const navigate = useNavigate();

  const filterPayments = (payments) => {
    setFilteredPayments((items) =>
      payments.filter((_, index) => index + 1 <= 10)
    );
  };

  const handleSearchQueries = (payments) => {
    return payments.filter((payment) => {
      const id = `${payment.id}` === query;
      const status = payment.status.toLowerCase().includes(query);
      const customerEmail = payment.payer_name
        ?.toLowerCase()
        .includes(query);

      return id || status || customerEmail;
    });
  };

  const handleSearch = () => {
    setFilteredPayments(() => {
      return handleSearchQueries(_payments);
    });
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const query = searchQuery.toLowerCase();
    setSearchParams({ q: query });

    handleSearch();
  };

  useEffect(() => {
    setFilteredPayments(() =>
      _payments.filter(
        (_, index) =>
          index + 1 > 10 * (pageIndex - 1) && index + 1 <= 10 * pageIndex
      )
    );

    if (query) {
      handleSearch();
    }
  }, [pageIndex, query]);

  const handleSort = (def) => {
    switch (def) {
      case "index": {
        setPageIndex(1);
        if (sorted !== "index") {
          setSorted("index");
        } else {
          setSorted("");
        }

        _payments.reverse();
        setPayments(_payments);
        filterPayments(_payments);
        return;
      }
      case "amount": {
        setPageIndex(1);
        if (sorted === "amount") {
          setSorted("");
          _payments.sort((a, b) => a.index - b.index);
        } else {
          setSorted("amount");
          _payments.sort((a, b) => {
            if (a.amount < b.amount) return -1;
            if (a.amount > b.amount) return 1;
            return 0;
          });
        }
        filterPayments(_payments);
        return;
      }
      case "customer": {
        setPageIndex(1);
        if (sorted === "customer") {
          setSorted("");
          _payments.sort((a, b) => a.index - b.index);
        } else {
          setSorted("customer");
          _payments.sort((a, b) => {
            if (a.payer_name < b.payer_name) return -1;
            if (a.payer_name > b.payer_name) return 1;
            return 0;
          });
        }
        filterPayments(_payments);
        return;
      }
      case "status": {
        setPageIndex(1);
        if (sorted === "status") {
          setSorted("");
          _payments.sort((a, b) => a.index - b.index);
        } else {
          setSorted("status");
          _payments.sort((a, b) => {
            if (a.payout_status < b.payout_status) return -1;
            if (a.payout_status > b.payout_status) return 1;
            return 0;
          });
        }
        filterPayments(_payments);
        return;
      }
      case "date": {
        setPageIndex(1);
        if (sorted === "date") {
          setSorted("");
          _payments.sort((a, b) => a.index - b.index);
        } else {
          setSorted("date");
          _payments.sort(
            (a, b) => new Date(a.created) - new Date(b.created)
          );
        }
        filterPayments(_payments);
        return;
      }
    }
  };

  return (
    <div className="mt-5 max-h-[83vh] overflow-y-scroll">
      <EarningsChart
        transactions={filteredPayments}
        value="payments"
        subtitle={`All Payments`}
        title={`All Payments Overview`}
        yOriginTitle={`All Payments ($)`}
      />
      <div className="border bg-white rounded-md overflow-x-scroll scrollbar-hide mt-4">
        <div className="flex justify-between p-2 items-center">
          <form className="relative" onSubmit={handleSearchSubmit}>
            <input
              className="outline-none h-[40px] w-[300px] border rounded-md p-2 placeholder placeholder:text-[14px] text-sm"
              placeholder="Search payment"
              name="query"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button
              className="absolute h-[38px] rounded-md top-1/2 -translate-y-1/2 right-[1px] w-[30px] flex items-center justify-center bg-white cursor-pointer hover:text-primary duration-300"
              type="submit"
            >
              <SearchNormal size={15} />
            </button>
          </form>

          <div className="flex gap-2 items-center">
            <PaginationButton
              item={<ArrowLeft2 size={16} />}
              disabled={pageIndex <= 1}
              handleClick={() => setPageIndex(pageIndex - 1)}
            />
            <span className="font-light text-xs">Page {pageIndex}</span>
            <PaginationButton
              item={<ArrowRight2 size={16} />}
              disabled={pageIndex >= Math.ceil(_payments.length / 10)}
              handleClick={() => setPageIndex((index) => index + 1)}
            />
          </div>
        </div>
        <div className="max-h-[80vh] overflow-y-scroll scrollbar-hide">
          <table className="w-full relative whitespace-nowrap">
            <thead>
              <tr className="uppercase h-14 font-bold bg-[#f1f1f1] text-gray-700 text-[12px]">
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("index")}
                >
                  #{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "index" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td className="px-6 py-4">Consultation ID</td>
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("amount")}
                >
                  Amount{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "amount" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td className="px-6 py-4">Earning</td>
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("status")}
                >
                  Payout Status{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "status" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("customer")}
                >
                  Customer{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "customer" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("date")}
                >
                  Date{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "date" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
              </tr>
            </thead>

            <tbody>
              {filteredPayments.length < 1 ? (
                <tr>
                  <td className="px-6 py-4" colSpan={6}>
                    No payments yet
                  </td>
                </tr>
              ) : (
                filteredPayments.map((payment, index) => (
                  <tr key={payment.id} className="h-14 text-[14px] border-b">
                    <td className="px-6 py-4">{payment.index}</td>
                    <td
                      className="px-6 py-4 underline text-primary cursor-pointer"
                      onClick={() =>
                        navigate(
                          `/dashboard/telemedicine/consultations/${payment.order_id}`
                        )
                      }
                    >
                      {payment.order_id}
                    </td>
                    <td className="px-6 py-4">
                      ${payment.amount.toLocaleString()}
                    </td>
                    <td className="px-6 py-4">
                      ${(payment.amount - payment.fee).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 capitalize">
                      {payment.payout_status}
                    </td>
                    <td className="px-6 py-4">{payment.payer_name}</td>
                    <td className="px-6 py-4">
                      {dayjs(payment.created_at).format("DD MMM, YYYY")}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
